import React, { useEffect, useMemo, useState } from "react";
import { fetchJson } from "../../lib/api";
import { flattenChartAccounts } from "../../lib/chart-utils";
import { formatAmountValue } from "../../lib/format-utils";
import type { BalanceSheetResponse } from "../../lib/types";
import { useI18n } from "../../i18n/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "../card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../table";

export const BalanceSheetView = ({
  houseId,
  search,
  setSearch,
}: {
  houseId: number | null;
  search: string;
  setSearch: (nextSearch: string, options?: { replace?: boolean }) => void;
}) => {
  const { t } = useI18n();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const [report, setReport] = useState<BalanceSheetResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!houseId) {
      setReport(null);
      return;
    }
    const yearParam = Number.parseInt(searchParams.get("year") ?? "", 10);
    const yearQuery = Number.isNaN(yearParam) ? "" : `?year=${yearParam}`;
    setIsLoading(true);
    setError(null);
    fetchJson<BalanceSheetResponse>(`/api/houses/${houseId}/balance-sheet${yearQuery}`)
      .then((response) => {
        setReport(response);
        if (Number.isNaN(yearParam) || response.year !== yearParam) {
          const nextParams = new URLSearchParams(search);
          nextParams.set("year", String(response.year));
          setSearch(`?${nextParams.toString()}`, { replace: true });
        }
      })
      .catch((loadError) => {
        setError(
          loadError instanceof Error
            ? loadError.message
            : t("reports.balanceSheetLoadFailed", "Failed to load report"),
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [houseId, search, searchParams, setSearch]);

  if (!houseId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("reports.balanceSheetTitle", "Balance sheet")}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted">
            {t("reports.balanceSheetSelectHouse", "Select a house to review the balance sheet.")}
          </p>
        </CardContent>
      </Card>
    );
  }

  const availableYears = report?.years.length ? report.years : report ? [report.year] : [];
  const activeYear = report?.year ?? new Date().getUTCFullYear();
  const flattenedRows = report ? flattenChartAccounts(report.accounts) : [];
  const reportCurrencies = useMemo(() => {
    const currencies = new Set<string>();
    flattenedRows.forEach(({ node }) => {
      if (node.currency) {
        currencies.add(node.currency);
      }
    });
    return Array.from(currencies).sort((left, right) => left.localeCompare(right, "en"));
  }, [flattenedRows]);
  const currencyLabel = reportCurrencies.length ? reportCurrencies.join(", ") : "DKK";

  const updateYear = (value: string) => {
    const nextParams = new URLSearchParams(search);
    nextParams.set("year", value);
    setSearch(`?${nextParams.toString()}`);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-lg font-semibold text-ink">
            {t("reports.balanceSheetTitle", "Balance sheet")}
          </p>
          <p className="text-sm text-muted">
            {t("reports.balanceSheetAsOf", "Asset balances as of {date}.", {
              date: report?.asOfDate ?? t("reports.selectedPeriod", "the selected period"),
            })}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-ink" htmlFor="bs-year">
              {t("common.year", "Year")}
            </label>
            <select
              id="bs-year"
              className="h-9 rounded-md border px-3 text-sm brand-input brand-focus"
              value={String(activeYear)}
              onChange={(event) => updateYear(event.target.value)}
              disabled={!availableYears.length}
            >
              {availableYears.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      {isLoading ? (
        <p className="text-sm text-muted">{t("reports.loading", "Loading report...")}</p>
      ) : null}
      {error ? <p className="text-sm text-error">{error}</p> : null}
      <Card>
        <CardHeader>
          <CardTitle>{t("reports.assets", "Assets")}</CardTitle>
        </CardHeader>
        <CardContent>
          {!report ? (
            <p className="text-sm text-muted">
              {t("reports.noData", "No report data available.")}
            </p>
          ) : flattenedRows.length === 0 ? (
            <p className="text-sm text-muted">
              {t("reports.noAssetAccounts", "No asset accounts configured yet.")}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="h-8 min-w-[120px] py-1">{t("accounts.account", "Account")}</TableHead>
                  <TableHead className="h-8 py-1">{t("accounts.name", "Name")}</TableHead>
                  <TableHead className="h-8 py-1 text-right">{t("accounts.balance", "Balance")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {flattenedRows.map(({ node, depth }) => {
                  const label = node.labelKey ? t(node.labelKey, node.name) : node.name;
                  return (
                    <TableRow key={node.code} className="h-8">
                      <TableCell className={node.isTotal ? "py-1 font-semibold" : "py-1"}>
                        {node.code}
                      </TableCell>
                      <TableCell
                        className={node.isTotal ? "py-1 font-semibold" : "py-1"}
                        style={{ paddingLeft: depth * 20 }}
                      >
                        {label}
                      </TableCell>
                      <TableCell className="py-1 text-right">
                        {formatAmountValue(node.balance)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      <p className="text-right text-xs text-muted">
        {t("reports.amountsInCurrency", "Amounts in {currency}.", { currency: currencyLabel })}
      </p>
    </div>
  );
};
